import { useMemo } from 'react';
import { DataTableRow } from './utils';

/**
 * Stamps a stable `__key` field on each row of `rows` so the result
 * can flow into `<DataTable getRowId>` — the project's `getRowId` is
 * single-arg, so a row index can't be folded into the key inline. The
 * mapping is wrapped in `useMemo` so a stable `rows` reference
 * produces a stable output array.
 *
 * Pass a stable `getKey` (module-level fn or `useCallback`) — an
 * inline arrow recreated each render will bust the memo.
 *
 * @param {Array} rows - Source data
 * @param {Function} getKey - Per-row key function `(row, idx) => string`
 * @return {Array} Rows widened with a `__key` field
 */
export const useKeyedRows = <T>(
  rows: T[],
  getKey: (row: T, idx: number) => string
): DataTableRow<T>[] =>
  useMemo(
    () =>
      rows.map(
        (row, idx) =>
          ({
            ...row,
            __key: getKey(row, idx),
          }) as DataTableRow<T>
      ),
    [rows, getKey]
  );
